import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { finishSignup, needsVerifyOffer } from '../lib/signupCompletion';

// Mounted in AppShell, so it runs on whichever screen a new member first lands
// on: /feed straight after joining (confirmation off), or wherever the
// confirmation link or their first sign-in takes them (confirmation on).
// Does nothing once pending_signup is cleared.
//
// ⚠️ The run must NOT be cancelled when `pending` goes false. finishSignup's
// last step clears pending_signup (auth.updateUser → USER_UPDATED → a new
// session without it), so `pending` flips while the run is still in flight.
// A cleanup tied to that flip dropped the redirect to /get-verified for every
// new DSP (2026-10-30). Only an unmount or a different user cancels.
export function useFinishSignup() {
  const { session, updateProfile, refreshProfile } = useUser();
  const navigate = useNavigate();
  const user = session?.user;
  const pending = !!user?.user_metadata?.pending_signup;

  const mounted = useRef(true);
  const currentUserId = useRef(user?.id);
  const startedFor = useRef(null);
  currentUserId.current = user?.id;

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!pending || startedFor.current === user.id) return;
    const runFor = user.id;
    startedFor.current = runFor;
    const stillHere = () => mounted.current && currentUserId.current === runFor;
    finishSignup(user, updateProfile).then(async (result) => {
      if (!result || !stillHere()) return;
      // Brings the terms record, zip consent and a code's verification.
      await refreshProfile();
      if (stillHere() && needsVerifyOffer(result)) navigate('/get-verified', { replace: true });
    });
  // user changes identity on every token refresh; the id is what matters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pending]);
}
